// ─────────────────────────────────────────────
//  БЛОК 2 «Заказ клиента» — статусы, условия оплаты, конфиг шаблона счёта
// ─────────────────────────────────────────────
//
// «Заказ клиента» (раздел «Заказы») — сущность Блока 2. Не путать с «Заказом
// поставщику» (Блок 3). Один заказ = одна заявка. Счёт мы НЕ выставляем — это
// делает 1С:УНФ; мы лишь готовим Excel-шаблон для загрузки в УНФ.
//
// Паттерн объявления — как в statuses.ts: `as const` + `typeof[keyof]` для типа.

// Статусы «Заказа клиента». Цепочка из ТЗ (раздел 6):
//   Новый → Ждёт согласования РОП (если плановая предоплата <50%) → Подтверждён
//        → Ждём оплату → Частично оплачен → Оплачен → Закрыт / Отказ → Архив
// Архив — сквозной статус (попасть можно из любого).
export const OrderStatus = {
  NEW: 'NEW',
  PENDING_ROP: 'PENDING_ROP',
  CONFIRMED: 'CONFIRMED',
  AWAITING_PAYMENT: 'AWAITING_PAYMENT',
  PARTIALLY_PAID: 'PARTIALLY_PAID',
  PAID: 'PAID',
  CLOSED: 'CLOSED',
  CANCELLED: 'CANCELLED',
  ARCHIVED: 'ARCHIVED',
} as const
export type OrderStatus = (typeof OrderStatus)[keyof typeof OrderStatus]

export const ORDER_STATUS_LABELS_RU: Record<OrderStatus, string> = {
  NEW: 'Новый',
  PENDING_ROP: 'Ждёт согласования РОП',
  CONFIRMED: 'Подтверждён',
  AWAITING_PAYMENT: 'Ждём оплату',
  PARTIALLY_PAID: 'Частично оплачен',
  PAID: 'Оплачен',
  CLOSED: 'Закрыт',
  CANCELLED: 'Отказ',
  ARCHIVED: 'Архив',
}

// Терминальные статусы заказа — дальше работы по заказу нет.
export const FINAL_ORDER_STATUSES: ReadonlySet<OrderStatus> = new Set([
  'CLOSED',
  'CANCELLED',
  'ARCHIVED',
])

/**
 * Статусы, в которых состав заказа можно менять (правка позиций, убрать и
 * добавить позицию) — до полной оплаты. Решение Сергея 29.09.2026 по обращению
 * помощника (заказчик одобрил); паспорт docs/features/order-editing.md.
 * «Оплачен», «Закрыт», «Отказ», «Архив» — только просмотр.
 */
export const ORDER_EDITABLE_STATUSES: ReadonlySet<OrderStatus> = new Set([
  'NEW',
  'PENDING_ROP',
  'CONFIRMED',
  'AWAITING_PAYMENT',
  'PARTIALLY_PAID',
])

export function isOrderEditable(status: OrderStatus): boolean {
  return ORDER_EDITABLE_STATUSES.has(status)
}

// Условие оплаты (плановое). От него зависит необходимость согласования РОП:
// согласование нужно, когда плановая предоплата <50% (см. needsRopApproval).
// Гарантийное письмо не обязательно — может быть прописано в договоре.
export const OrderPaymentTerms = {
  PREPAYMENT: 'PREPAYMENT',
  GUARANTEE_LETTER: 'GUARANTEE_LETTER',
  DEFERRED: 'DEFERRED',
  POSTPAYMENT: 'POSTPAYMENT',
} as const
export type OrderPaymentTerms = (typeof OrderPaymentTerms)[keyof typeof OrderPaymentTerms]

export const ORDER_PAYMENT_TERMS_LABELS_RU: Record<OrderPaymentTerms, string> = {
  PREPAYMENT: 'Предоплата',
  GUARANTEE_LETTER: 'Гарантийное письмо',
  DEFERRED: 'Отсрочка по договору',
  POSTPAYMENT: 'Постоплата',
}

// Статус счёта-шаблона. Счёт = подготовленный нами Excel-шаблон + метаданные;
// сам счёт (номер, печатная форма) формирует 1С:УНФ.
//   DRAFT — шаблон создан, но ещё не зафиксирован
//   ISSUED — шаблон выгружен (счёт «выставлен»)
//   PAID — по счёту прошла полная оплата
//   CANCELLED — счёт аннулирован
export const OrderInvoiceStatus = {
  DRAFT: 'DRAFT',
  ISSUED: 'ISSUED',
  PAID: 'PAID',
  CANCELLED: 'CANCELLED',
} as const
export type OrderInvoiceStatus = (typeof OrderInvoiceStatus)[keyof typeof OrderInvoiceStatus]

export const ORDER_INVOICE_STATUS_LABELS_RU: Record<OrderInvoiceStatus, string> = {
  DRAFT: 'Черновик',
  ISSUED: 'Выставлен',
  PAID: 'Оплачен',
  CANCELLED: 'Аннулирован',
}

// Статус платежа. TODO (В11): авто-сверка оплат по приходам из банка/УНФ —
// пока только ручная фиксация (PLANNED/RECEIVED).
export const OrderPaymentStatus = {
  PLANNED: 'PLANNED',
  RECEIVED: 'RECEIVED',
  CANCELLED: 'CANCELLED',
} as const
export type OrderPaymentStatus = (typeof OrderPaymentStatus)[keyof typeof OrderPaymentStatus]

export const ORDER_PAYMENT_STATUS_LABELS_RU: Record<OrderPaymentStatus, string> = {
  PLANNED: 'Запланирован',
  RECEIVED: 'Получен',
  CANCELLED: 'Отменён',
}

// Тип документа заказа (order_documents).
export const OrderDocumentKind = {
  INVOICE_TEMPLATE: 'INVOICE_TEMPLATE',
  GUARANTEE_LETTER: 'GUARANTEE_LETTER',
  PAYMENT_PROOF: 'PAYMENT_PROOF',
  INVOICE_PDF: 'INVOICE_PDF',
  OTHER: 'OTHER',
} as const
export type OrderDocumentKind = (typeof OrderDocumentKind)[keyof typeof OrderDocumentKind]

export const ORDER_DOCUMENT_KIND_LABELS_RU: Record<OrderDocumentKind, string> = {
  INVOICE_TEMPLATE: 'Шаблон счёта (Excel)',
  GUARANTEE_LETTER: 'Гарантийное письмо',
  PAYMENT_PROOF: 'Платёжное поручение',
  INVOICE_PDF: 'Счёт из УНФ (PDF)',
  OTHER: 'Прочее',
}

// Юрлицо-продавец (решение Р1, stages-3-5-plan.md; МОСЭЛ подтверждён
// Сергеем 13.07.2026). Хранится в order_invoices.entityId и, с 23.09.2026, в
// requests.sellerEntityId (юрлицо продажи выбирается при создании заявки и
// подставляется в КП и счёт). Каноничный список здесь; полные реквизиты для КП
// (ИНН/адрес/логотип) живут на фронте в kpEntities.ts. У МОСЭЛ — свой бланк КП
// (из бланка РОЗ 25.09.2026), в самом документе он «ООО «МосЭл»».
export const SellerEntity = {
  SPM: 'SPM',
  DISTINCT: 'DISTINCT',
  MOSEL: 'MOSEL',
} as const
export type SellerEntity = (typeof SellerEntity)[keyof typeof SellerEntity]

export const SELLER_ENTITY_LABELS_RU: Record<SellerEntity, string> = {
  SPM: 'ООО «СПЕЦМАШЭЛЕКТРО»',
  DISTINCT: 'ООО «ДИСТИНКТ»',
  MOSEL: 'ООО «МОСЭЛ»',
}

/**
 * ИНН юрлиц-продавцов: по ИНН поставщика в счёте 1С определяется юрлицо
 * (docs/features/unf-invoice-upload.md). Те же ИНН — в реквизитах КП (kpEntities.ts).
 */
export const SELLER_ENTITY_INN: Record<SellerEntity, string> = {
  SPM: '7813266433',
  DISTINCT: '7814837898',
  MOSEL: '7733464038',
}

export function sellerEntityByInn(inn: string | null | undefined): SellerEntity | null {
  const digits = (inn ?? '').replace(/\D/g, '')
  const hit = (Object.keys(SELLER_ENTITY_INN) as SellerEntity[]).find(
    (k) => SELLER_ENTITY_INN[k] === digits,
  )
  return hit ?? null
}

/** Код юрлица из списка SellerEntity (строка из БД/API может быть чем угодно). */
export function isSellerEntity(value: unknown): value is SellerEntity {
  return typeof value === 'string' && Object.hasOwn(SELLER_ENTITY_LABELS_RU, value)
}

// Порог плановой предоплаты, ниже которого требуется согласование РОП.
// Предоплата ≥50% (или полная) → согласование не нужно, заказ сразу «Подтверждён».
// <50% (включая отсрочку/постоплату, гарантийное письмо без предоплаты) → «Ждёт
// согласования РОП». Админ может переопределить решение РОПа.
export const ROP_APPROVAL_THRESHOLD_PERCENT = 50

export function needsRopApproval(prepaymentPercent: number): boolean {
  if (!Number.isFinite(prepaymentPercent)) return true
  return prepaymentPercent < ROP_APPROVAL_THRESHOLD_PERCENT
}

// Потолок денежных колонок заказов, счетов и оплат: Decimal(14,2) в БД =
// 10^12 − 0,01 ₽. Реальные счета — до сотен миллионов ₽, запас больше ×1000.
// Проверка «цена × количество» (S500-10, пакет 5): по отдельности цена и
// количество проходят свои границы, а произведение могло не влезть в колонку.
export const MAX_ORDER_MONEY_RUB = 999_999_999_999.99

/**
 * Допустимые переходы статуса «Заказа клиента» (Блок 2). ЕДИНАЯ матрица для
 * бэка (assertOrderTransition в orders.workflow.ts) и фронта (кнопки
 * OrderStatusActions) — раньше фронт дублировал граф вручную и мог молча
 * разойтись (Г18, 02.07.2026).
 *
 * Цепочка: NEW → PENDING_ROP → CONFIRMED → AWAITING_PAYMENT → PARTIALLY_PAID
 *          → PAID → CLOSED; CANCELLED и ARCHIVED — сквозные «нет/архив».
 * NEW — транзитный: при создании заказ сразу резолвится в PENDING_ROP (<50%
 * предоплаты) или CONFIRMED. Переходы оплаты проставляет recompute по сумме
 * платежей. Возврат CANCELLED→CONFIRMED намеренно ОТСУТСТВУЕТ в матрице —
 * это админский override только через POST /orders/:id/approve.
 */
export const ORDER_TRANSITIONS: Record<OrderStatus, readonly OrderStatus[]> = {
  NEW: ['PENDING_ROP', 'CONFIRMED', 'CANCELLED', 'ARCHIVED'],
  PENDING_ROP: ['CONFIRMED', 'CANCELLED', 'ARCHIVED'],
  CONFIRMED: ['AWAITING_PAYMENT', 'PARTIALLY_PAID', 'PAID', 'CANCELLED', 'ARCHIVED'],
  AWAITING_PAYMENT: ['PARTIALLY_PAID', 'PAID', 'CANCELLED', 'ARCHIVED'],
  PARTIALLY_PAID: ['PAID', 'CANCELLED', 'ARCHIVED'],
  PAID: ['CLOSED', 'ARCHIVED'],
  CLOSED: ['ARCHIVED'],
  CANCELLED: ['ARCHIVED'],
  ARCHIVED: [],
}

// Конфигурируемый набор колонок Excel-шаблона счёта для загрузки в 1С:УНФ.
// Форма подтверждена практикой (02.07.2026): этот Excel штатно загружается в
// УНФ заказчика. Меняется в ОДНОМ месте: и бэк (буфер строк), и фронт (генерация
// xlsx) читают этот список. `width` — ширина колонки в символах для ExcelJS.
export interface OrderInvoiceColumn {
  key: string
  label: string
  width: number
}

export const ORDER_INVOICE_COLUMNS: ReadonlyArray<OrderInvoiceColumn> = [
  // «Наименование» и «Артикул» берутся из справочника номенклатуры 1С
  // (nomenclature_ref) 1-в-1, если парт там найден; иначе — по правилам fallback
  // (см. resolveUnfNaming). Это нужно, чтобы УНФ не создавал дубли номенклатуры.
  { key: 'name', label: 'Наименование', width: 44 },
  { key: 'article', label: 'Артикул', width: 28 },
  { key: 'brand', label: 'Производитель', width: 18 },
  { key: 'quantity', label: 'Кол-во', width: 10 },
  { key: 'unit', label: 'Ед.', width: 8 },
  { key: 'price', label: 'Цена, ₽', width: 14 },
  // УНФ-классификация (с 13.07.2026): без неё позиция вне справочника УНФ
  // грузится «Новой номенклатурой» без категории. Новые столбцы — строго
  // В КОНЕЦ (существующая форма подтверждена практикой, порядок не менять).
  { key: 'unfCategory', label: 'Категория', width: 30 },
  { key: 'unfGroup', label: 'Входит в группу', width: 14 },
]
